import React from 'react'

import { useFlags } from 'launchdarkly-react-client-sdk'

import { ShowForUS } from 'src/shared/components/common/show_for_us/ShowForUS'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import { PackagingOptions } from 'public/components/default_template/online_ordering/checkout/packaging/PackagingOptions'
import CreditCardFormSPI from 'public/components/default_template/online_ordering/checkout/payment/CreditCardFormSPI'
import Fundraising from 'public/components/default_template/online_ordering/checkout/payment/Fundraising'
import OrderPrices from 'public/components/default_template/online_ordering/checkout/payment/OrderPrices'
import PaymentOptionsSection from 'public/components/default_template/online_ordering/checkout/payment/PaymentOptionsSection'
import { AcceptedPaymentMethods } from 'public/components/default_template/online_ordering/checkout/payment/SavedCreditCards'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import {
  PaymentOption,
  usePayment
} from 'public/components/online_ordering/PaymentContext'
import ToastProduct from 'public/components/online_ordering/ToastProduct'

import CheckoutSection from './CheckoutSection'
import { t } from 'src/translations'

interface PaymentSectionProps {
  isExpanded: boolean
}

const PaymentSection = () => {
  const { ooRestaurant } = useRestaurant()
  const { cart } = useCart()
  const { paymentOption } = usePayment()
  const { enabledPaymentOptions } = useCheckout()
  const canPayAtCheckout =
    !enabledPaymentOptions.has(PaymentOption.UponReceipt) ||
    enabledPaymentOptions.size > 1

  if (!ooRestaurant || !cart) {
    return null
  }

  return (
    <>
      <CheckoutSection title={t('payment-section.payment')}>
        {canPayAtCheckout && <AcceptedPaymentMethods />}
        <PaymentOptionsSection />
        <ShowForUS>
          <CreditCardInfo paymentOption={paymentOption} />
        </ShowForUS>
      </CheckoutSection>
    </>
  )
}

export const TipAndPricesSection = ({ isExpanded }: PaymentSectionProps) => {
  const { ooRestaurant, toastProduct } = useRestaurant()
  const { cart } = useCart()
  const { fundraisingEnabled } = usePayment()
  const { setPackagingOptions, packagingOptions } = useCheckout()

  if (!ooRestaurant || !cart) {
    return null
  }

  return (
    <>
      {ooRestaurant?.packagingConfig?.enabled &&
        toastProduct !== ToastProduct.OOEcommerce && (
          <CheckoutSection title={t('payment-section.packaging-options')}>
            <PackagingOptions
              packagingConfig={ooRestaurant.packagingConfig}
              selectedPackagingOptions={packagingOptions}
              onPackagingOptionsUpdate={setPackagingOptions}
            />
          </CheckoutSection>
        )}
      {fundraisingEnabled && (
        <CheckoutSection>
          <FundraisingSection />
        </CheckoutSection>
      )}
      <OrderPrices canShowPaymentAdjustments={isExpanded} />
    </>
  )
}

const CreditCardInfo = ({
  paymentOption
}: {
  paymentOption: PaymentOption | null
}) => {
  const { ooToastCashSpend: ooToastCashSpendEnabled } = useFlags()
  const { orderTotal, enabledPaymentOptions } = useCheckout()

  return (
    <AnimatedSection expanded={paymentOption === PaymentOption.PayNow}>
      <CreditCardFormSPI
        allowToastCash={ooToastCashSpendEnabled}
        disableSPI={orderTotal <= 0}
        canPayAtRestaurant={enabledPaymentOptions.has(
          PaymentOption.UponReceipt
        )}
      />
    </AnimatedSection>
  )
}

const FundraisingSection = () => {
  const { ooRestaurant } = useRestaurant()
  const { cart } = useCart()
  const {
    tipAmount,
    fundraisingEnabled,
    setFundraisingAmount,
    fundraisingAmount
  } = usePayment()
  const fundraisingCampaign = ooRestaurant?.fundraisingConfig ?? undefined
  return (
    <Fundraising
      fundraisingCampaign={fundraisingCampaign}
      onChange={(amount: number) => setFundraisingAmount(amount)}
      orderTotal={(cart?.order?.totalV2 || 0) + tipAmount}
      fundraisingEnabled={fundraisingEnabled}
      fundraisingAmount={fundraisingAmount}
    />
  )
}

export default PaymentSection
